import React, { PropsWithChildren } from "react";
import { observer } from "mobx-react";
import type { IResourcesStore } from "../../../../resources/ResourcesStore";
import { injectFromApp } from "../../../../shared-components/inject";
import type { IUploadStore } from "../../../UploadStore";
import { injectFromAppUpload } from "../../../inject";
import { UploadStatus } from "../../../FileUpload";
import type { PatientToUpload } from "../../../PatientToUpload";
import { StaticNotificationComponent } from "../../../../shared-components/static-notification/StaticNotificationComponent";
import { humanizeFileSize } from "../../../../util/functions/humanizeFileSize";
import { assertUnreachable } from "../../../../util/assertions/assertUnreachable";
import { ButtonRichToolTipComponent } from "../../../../shared-components/rich-tooltip/RichToolTipComponent";
import type { ICurrentUploadSettingsStore } from "../../../CurrentUploadSettingsStore";

interface IUploadButtonLogicComponentProps {
    resourcesStore?: IResourcesStore;
    uploadStore?: IUploadStore;
    patientToUpload: PatientToUpload;
    currentUploadUserSettingsStore?: ICurrentUploadSettingsStore;
}

@injectFromApp<IUploadButtonLogicComponentProps>((stores) => ({
    resourcesStore: stores.resourcesStore,
}))

@injectFromAppUpload<IUploadButtonLogicComponentProps>((stores) => ({
    uploadStore: stores.uploadStore,
    currentUploadUserSettingsStore: stores.currentUploadSettingsStore,
}))

@observer
export class UploadButtonLogicComponent extends React.Component<PropsWithChildren<IUploadButtonLogicComponentProps>> {
    startQueueingUpload = (event) => {
        const { patientToUpload } = this.props;
        patientToUpload.startQueueingUpload();
    };

    pauseUpload = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.pauseUpload();
    };

    cancelUpload = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.cancelUpload();
    };

    resumeUpload = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.resumeUpload();
    };

    resetFiles = (event): void => {
        const { uploadStore } = this.props;
        uploadStore!.resetFiles();
    };

    render() {
        const { resourcesStore, patientToUpload, uploadStore, currentUploadUserSettingsStore } = this.props;
        const { resources } = resourcesStore!;
        const { patientConsent, privacyPolicyConsent, readyToUpload, status, reachedMaxToUploadSize, filesToUpload, filesToUploadTotalSize } = patientToUpload;
        const { allUploadsDone, maxUploadSize } = uploadStore!;
        const { maximumAllowedBytesUploadedPerUploadCode } = currentUploadUserSettingsStore!;

        if (reachedMaxToUploadSize) {
            return (
                <StaticNotificationComponent type={"error"} title={resources.UploadErrorExceedsSize}>
                    {resources.XFiles.replace("{0}", filesToUpload.length.toString())} ({humanizeFileSize(filesToUploadTotalSize)})
                    <br />
                    {resources.UploadErrorExceedsSizeInfo.replace("{0}", humanizeFileSize(maxUploadSize))}
                    {this.props.children}
                </StaticNotificationComponent>
            );
        }

        switch (status) {
            case UploadStatus.NotStarted:
                return (
                    <StaticNotificationComponent
                        type={"info"}
                        title={!patientConsent || !privacyPolicyConsent ? resources.AgreeToPrivacyAndPatientConsent : resources.ReadyToUpload}
                        hideIcon={true}
                    >
                        <ButtonRichToolTipComponent
                            toolTipResource={!patientConsent || !privacyPolicyConsent ? "AgreeToPrivacyAndPatientConsentInfo" : undefined}
                            type="button"
                            className={"green start"}
                            disabled={!readyToUpload}
                            onClick={this.startQueueingUpload}
                        >
                            {resources.StartUpload}
                        </ButtonRichToolTipComponent>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.Paused:
                return (
                    <StaticNotificationComponent type={"warning"} title={resources.UploadIsPaused} customClass={"paused"}>
                        <button type="button" className={"silent border resume"} onClick={this.resumeUpload}>
                            {resources.ResumeUpload}
                        </button>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.Started:
            case UploadStatus.Queued:
                return (
                    <StaticNotificationComponent type={"info"} title={resources.Uploading} customClass={"loading"}>
                        <button type="button" className={"silent border pause"} onClick={this.pauseUpload}>
                            {resources.PauseUpload}
                        </button>
                        <button type="button" className={"silent border delete"} onClick={this.cancelUpload}>
                            {resources.CancelUpload}
                        </button>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.ValidatingFiles:
                return (
                    <StaticNotificationComponent type={"info"} title={resources.ValidatingFiles} customClass={"loading"}>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.Done:
                return (
                    <StaticNotificationComponent type={"success"} title={resources.UploadSuccessful}>
                        {allUploadsDone && (
                            <button className={"silent border"} onClick={this.resetFiles}>
                                {resources.StartANewUpload}
                            </button>
                        )}
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.MaliciousContent:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.MaliciousContent}>
                        {resources.ImagesRemovedAndUploadIsCancelled}
                        <br />
                        <br />
                        <button className={"silent border"} onClick={this.resetFiles}>
                            {resources.StartANewUpload}
                        </button>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.NotADicomFile:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.OnlyDicomFilesAllowed}>
                        {resources.ImagesRemovedAndUploadIsCancelled}
                        <br />
                        <br />
                        <button className={"silent border"} onClick={this.resetFiles}>
                            {resources.StartANewUpload}
                        </button>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.Failed:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.SomethingWentWrong}>
                        {resources.SomethingWentWrongInfo}
                        <br />
                        <br />
                        <button type="button" className={"border silent reload"} onClick={this.startQueueingUpload}>
                            {resources.RetryUpload}
                        </button>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.RateLimited:
                return (
                    <StaticNotificationComponent type={"warning"} title={resources.RateLimited}>
                        {resources.RateLimitedInfo}
                        <br />
                        {resources.PleaseTryAgainShortly}
                        <br />
                        {resources.SomethingWentWrongInfo}
                        <br />
                        <br />
                        <button type="button" className={"border silent reload"} onClick={this.startQueueingUpload}>
                            {resources.RetryUpload}
                        </button>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.ExceededUploadSizeLimit:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.ExceededUploadSizeLimit}>
                        {resources.ExceededUploadSizeLimitInfo.replace("{0}", humanizeFileSize(maximumAllowedBytesUploadedPerUploadCode))}
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.IpBlocked:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.IpBlocked}>
                        {resources.IpBlockedInfo}
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.UploadCodeExpired:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.UploadCodeExpired}>
                        {resources.UploadCodeExpiredInfo}
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.Cancelled:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.UploadIsCancelled}>
                        <button className={"silent border"} onClick={this.resetFiles}>
                            {resources.StartANewUpload}
                        </button>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            default:
                //Should never happen
                assertUnreachable(status);
                return null;
        }
    }
}
